import { SettingOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import { Breadcrumb, Button, ConfigProvider, Pagination, Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { NodataTable } from '../../../components/NoDataTable';
import { CustomLoadingOverlayTable, dataPagination, formatPrice, getWidthFromHeaderV2, renderValueAsJSX } from '../../../until';
import viVN from 'antd/locale/vi_VN';
import { formatNumber } from '../../../services/mixin';
import { ColDef } from 'ag-grid-community';
import Layout from '../../../Layout';
import { getInforCustomerComplaintBusinessDdmsByUnit, getInforCustomerComplaintByBusinessType, getInforCustomerComplaintByBusinessTypeAndBrand, getInforCustomerComplaintOtherIpccByUnit, getInforCustomerComplaintServiceDdmsByUnit, getInforCustomerStatisticsComplaint, getInforCustomerTopComplaintChannel, getReportComplaintDetailList } from '../../../api/reportCustomer/reportCustomerApi';
import { getListFieldsByCustomer, ListField } from '../../../api/Customer_V2/customerApi_V2';
import useDebounce from '../../../hook/useDebounce';
import useLoading from '../../../hook/useLoading';
import useToast from '../../../hook/useToast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FIELDTYPE, NO_VIEW } from '../../../services/data';
import CustomerFeilds from '../../CustomersV2/Drawer/CustomerFeilds';
import { FlattenedField } from '../../CustomersV2/indexV2';
import { useSelector } from 'react-redux';
import { selectorInfoUser } from '../../../store/slices/infoUserSlice';

// Row type sau khi map fields[] thành flat object
interface ReportComplaintFieldRow {
    id: number;
    [fieldCode: string]: any;
}

const RECORD_ID = FIELDTYPE.CUSTOMER;

const ReportComplaintDetailList = () => {

    const pushToast = useToast()
    const pushLoading = useLoading()
    const location = useLocation();
    const navigate = useNavigate();
    const { unitTemp } = useSelector(selectorInfoUser)

    const {
        unitId,
        businessType,
        categoryId,
        brandId,
        channel,
        group,
        dateCreate,
        listUnit,
        top,
        title,
        apiType,
    } = location?.state ?? {}

    const [loadingTable, setLoadingTable] = useState(false)
    const [dataList, setDataList] = useState<ReportComplaintFieldRow[]>([])
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // ── Cấu hình cột động ──
    const [isSettingShow, setSettingShow] = useState(false)
    const [dataFilterActive, setDataFilterActive] = useState<FlattenedField[]>([])
    const [dataFilterNoActive, setDataFilterNoActive] = useState<ListField[]>([])
    const [total, setTotal] = useState<number>()

    // Cột động từ danh sách trường active
    const columnDefs = useMemo<ColDef<ReportComplaintFieldRow>[]>(() => {
        return dataFilterActive.map((field) => ({
            headerName: field.fieldLabel,
            field: field.fieldCode,
            colId: field.fieldCode,
            minWidth: getWidthFromHeaderV2(field.fieldLabel),
            cellRenderer: (params: any) => {
                if (field.fieldCode !== 'cus_name') {
                    return renderValueAsJSX(params.value, field.fieldCode);
                }

                const { id } = params.data;
                const fullName = params.value;
                return (
                    <Tooltip
                        placement="right"
                        color="white"
                        title={
                            <span className="custom-page-css" style={{ color: "#000", textTransform: 'capitalize' }}>
                                {fullName}
                            </span>
                        }
                    >
                        <Link
                            style={{ color: "#1890ff", textTransform: 'capitalize' }}
                            to="/customer/list/edit"
                            state={{
                                firstPage: location.pathname,
                                id,
                                backState: location.state,
                            }}
                        >
                            {fullName}
                        </Link>
                    </Tooltip>
                );
            },
        }));
    }, [dataFilterActive, location, pagination.current, pagination.pageSize]);

    // ── Lấy field definitions ──
    const gListFieldsActive = async () => {
        pushLoading(true)
        const result = await getListFieldsByCustomer(RECORD_ID, 1)
        if (result?.status) {
            const flattenedFields: FlattenedField[] = result.data.flatMap(choice =>
                choice.fields.map(field => ({
                    ...field,
                    parentId: choice.id,
                }))
            );
            setDataFilterActive(flattenedFields)
            gListFieldsNoActive()
        }
        pushLoading(false)
    }

    const gListFieldsNoActive = async () => {
        const result = await getListFieldsByCustomer(RECORD_ID, 0)
        if (result?.status) {
            setDataFilterNoActive(result.data)
        }
    }

    const onSaveDataFields = (
        iSsuccess?: boolean,
        message?: string,
        updatedActive?: FlattenedField[],
        updatedInactive?: ListField[]
    ) => {
        if (iSsuccess) {
            setSettingShow(false)
            pushToast(message, "success")
            setDataFilterActive(updatedActive ?? [])
            setDataFilterNoActive(updatedInactive ?? [])
        } else {
            pushToast(message, "warning")
        }
    }

    // ── Gọi API danh sách chi tiết ──
    const fetchApi = (limit: number, pageNumber: number) => {
        if (apiType === 'topComplaintChannel') {
            return getInforCustomerTopComplaintChannel(channel, dateCreate, listUnit, unitTemp, top, limit, pageNumber);
        }
        if (apiType === 'statisticsComplaint') {
            return getInforCustomerStatisticsComplaint(group, dateCreate, listUnit, unitTemp, limit, pageNumber);
        }
        if (apiType === 'complaintByBusinessType') {
            return getInforCustomerComplaintByBusinessType(businessType, dateCreate, listUnit, unitTemp, limit, pageNumber);
        }
        if (apiType === 'businessDdmsByUnit') {
            return getInforCustomerComplaintBusinessDdmsByUnit(unitId, categoryId, dateCreate, listUnit, unitTemp, limit, pageNumber);
        }
        if (apiType === 'otherDdmsByUnit') {
            return getInforCustomerComplaintOtherIpccByUnit(unitId, categoryId, dateCreate, listUnit, unitTemp, limit, pageNumber);
        }
        if (apiType === 'serviceDdmsByUnit') {
            return getInforCustomerComplaintServiceDdmsByUnit(unitId, categoryId, dateCreate, listUnit, unitTemp, limit, pageNumber);
        }
        if (apiType === 'businessTypeAndBrand') {
            return getInforCustomerComplaintByBusinessTypeAndBrand(categoryId, brandId, dateCreate, listUnit, unitTemp, limit, pageNumber);
        }
        return getReportComplaintDetailList(unitId, businessType, dateCreate, listUnit, unitTemp, limit, pageNumber);
    };

    const gListComplaintDetail = useDebounce(async (limit: number = 10, pageNumber: number = 1) => {
        if (apiType === 'topComplaintChannel' && !channel) return;
        if (apiType === 'statisticsComplaint' && !group) return;
        if (apiType === 'complaintByBusinessType' && !businessType) return;
        if ((apiType === 'businessDdmsByUnit' || apiType === 'serviceDdmsByUnit' || apiType === 'otherDdmsByUnit') && (!unitId || !categoryId)) return;
        if (apiType === 'businessTypeAndBrand' && (!categoryId || !brandId)) return;
        if (!apiType && !unitId) return;
        setLoadingTable(true)

        const result = await fetchApi(limit, pageNumber);
        if (result?.status) {
            // Map fields[] thành flat row (giống CustomerList_V2)
            const mapped: ReportComplaintFieldRow[] = (result.data ?? []).map((item: any) => {
                const fieldValues: Record<string, any> = {};
                if (item.fields?.length) {
                    item.fields.forEach((f: any) => {
                        fieldValues[f.fieldCode] = f.view === false ? NO_VIEW : f.value;
                    });
                }
                return { ...item, ...fieldValues };
            });
            setDataList(mapped)
            setPagination({ current: pageNumber, total: result.total, pageSize: limit })
            setTotal(result.total)
        } else {
            pushToast(result?.message, "error", 2000)
        }
        setLoadingTable(false)
    }, 1000)

    const handleChangePagination = (current: number, pageSize: number) => {
        setPagination((prev) => ({ ...prev, current, pageSize }))
        gListComplaintDetail(pageSize, current)
    }

    useEffect(() => {
        gListFieldsActive()
    }, [])

    useEffect(() => {
        const hasValidParams =
            apiType === 'topComplaintChannel' ? !!channel :
            apiType === 'statisticsComplaint' ? !!group :
            apiType === 'complaintByBusinessType' ? !!businessType :
            (apiType === 'businessDdmsByUnit' || apiType === 'serviceDdmsByUnit' || apiType === 'otherDdmsByUnit') ? (!!unitId && !!categoryId) :
            apiType === 'businessTypeAndBrand' ? (!!categoryId && !!brandId) :
            unitId !== undefined;
        if (!hasValidParams) {
            navigate(-1)
        } else {
            gListComplaintDetail()
        }
    }, [location])

 useEffect(() => {
        gListComplaintDetail()
    }, [unitTemp])

    return (
        <div className='card-issuance-list'>
            <Layout>

                {isSettingShow && (
                    <CustomerFeilds
                        isSettingShow={isSettingShow}
                        recordId={RECORD_ID}
                        dataFilterActive={dataFilterActive}
                        dataFilterNoActive={dataFilterNoActive}
                        onSaveDataFields={onSaveDataFields}
                        onClose={() => setSettingShow(false)}
                    />
                )}

                <Breadcrumb
                    items={[
                        { title: 'THACO AUTO CRM' },
                        { title: <a href="/">Báo cáo phân tích</a> },
                        { title: 'Tổng quan phàn nàn/ khiếu nại' },
                        { title: 'Chi tiết khiếu nại' },
                    ]}
                />

                <div className='custom-page-css flex items-center gap-2'>
                    <Link to="/analysis-report/overview-complaints-grievances" className="icon-svg pb-1">
                        <svg width="1em" height="1em" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.6564 7.80321H4.04305L10.3955 2.37463C10.4971 2.28713 10.4354 2.12463 10.3012 2.12463H8.69582C8.62508 2.12463 8.55796 2.14963 8.50536 2.19428L1.65045 8.04963C1.58767 8.10321 1.53732 8.16944 1.50281 8.24385C1.4683 8.31825 1.45044 8.39909 1.45044 8.48088C1.45044 8.56268 1.4683 8.64352 1.50281 8.71792C1.53732 8.79233 1.58767 8.85856 1.65045 8.91213L8.54526 14.8032C8.57247 14.8264 8.60512 14.8389 8.63959 14.8389H10.2993C10.4336 14.8389 10.4953 14.6746 10.3937 14.5889L4.04305 9.16035H14.6564C14.7363 9.16035 14.8016 9.09606 14.8016 9.01749V7.94606C14.8016 7.86749 14.7363 7.80321 14.6564 7.80321Z" fill="currentColor" fillOpacity="0.88" />
                        </svg>
                    </Link>
                    <span className='title-page'>Danh sách khách hàng ({formatPrice(total)})</span>
                </div>

                <div className="custom-page-css w-full flex items-center justify-between pb-2">
                    <span>{title}</span>
                    <Button
                        icon={<SettingOutlined />}
                        onClick={() => setSettingShow(true)}
                    />
                </div>

                <div className="t-table-block custom-page-css flex flex-col flex-1 justify-between">
                    <div className="ag-theme-alpine empty-test-td custom-table-ag-v2" style={{ width: '100%' }}>
                        <AgGridReact<ReportComplaintFieldRow>
                            domLayout="autoHeight"
                            rowData={dataList}
                            columnDefs={columnDefs}
                            defaultColDef={{ resizable: true, sortable: false }}
                            loading={loadingTable}
                            getRowId={(params) => String(params.data.id)}
                            suppressDragLeaveHidesColumns
                            loadingOverlayComponent={CustomLoadingOverlayTable}
                            overlayNoRowsTemplate={NodataTable}
                            rowHeight={55}
                            getRowStyle={(params: any) => ({
                                backgroundColor: params?.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
                            })}
                        />
                    </div>
                    <ConfigProvider locale={viVN}>
                        <div className="pagination-block flex justify-center">
                            <Pagination
                                total={pagination.total}
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                showSizeChanger
                                itemRender={(page, type, originalElement) => {
                                    if (type === 'page') {
                                        return <span>{formatNumber(page)}</span>;
                                    }
                                    return originalElement;
                                }}
                                showTotal={(total) => `Tổng cộng ${dataPagination(pagination.total, pagination.pageSize, pagination.current).totalPage} trang`}
                                locale={{ items_per_page: 'trang' }}
                                onChange={handleChangePagination}
                            />
                        </div>
                    </ConfigProvider>
                </div>

            </Layout>
        </div>
    )
}
export default ReportComplaintDetailList
